import { classes } from "portal/theme/theme";
import {
  CropThumbnailRequest,
  ImageOp,
  imageServiceApi,
} from "portal/state/imageServiceApi";
import { Dimensions } from "portal/utils/categoryCollection/image";
import { Image, ImageProperties, ImageSrcErrorResult } from "./Image";
import { isAwsPresignedUrlExpired, splitS3Url } from "portal/utils/images";
import { skipToken } from "@reduxjs/toolkit/query";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import React, {
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useRef,
} from "react";

/**
 * Component that wraps around <Image/>
 * that handles making a crop thumbnail request to the image
 */
export type ThumbnailImageItem = ImageProperties &
  Omit<CropThumbnailRequest, "bucket" | "key" | "op"> & {
    url: string;
  };

interface ThumbnailImageProps {
  image?: ThumbnailImageItem;
  onImageLoaded?: (id?: string) => void;
  renderErrorOverlay?: () => ReactNode;
  className?: string;
}

export const assembleImageRequestParameters = ({
  url,
  x,
  y,
  width,
  height,
  fill,
}: Pick<
  ThumbnailImageItem,
  "url" | "x" | "y" | "width" | "height" | "fill"
>): CropThumbnailRequest => {
  const { bucket, key } = splitS3Url(url);
  return {
    op: ImageOp.CROP,
    bucket,
    key,
    x,
    y,
    width,
    height,
    fill: fill ?? "#000000",
  };
};

// Prefetch slightly before the tile enters the viewport.
const THUMBNAIL_VISIBILITY_ROOT_MARGIN = "200px";

export const ThumbnailImage: FC<ThumbnailImageProps> = ({
  image,
  onImageLoaded,
  renderErrorOverlay,
  className,
}) => {
  const [visibilityRef, intersection] = useIntersectionObserver<HTMLDivElement>(
    {
      rootMargin: THUMBNAIL_VISIBILITY_ROOT_MARGIN,
    }
  );
  const hasBeenVisibleRef = useRef(false);
  if (intersection?.isIntersecting) {
    hasBeenVisibleRef.current = true;
  }
  const lastExpiryRefetchSrc = useRef<string>();

  const shouldFetch = Boolean(image) && hasBeenVisibleRef.current;
  const {
    data: fetchedImageSource,
    isFetching: isFetchingImageSource,
    error: fetchingImageSourceError,
    refetch,
  } = imageServiceApi.endpoints.getCropThumbnail.useQuery(
    shouldFetch && image ? assembleImageRequestParameters(image) : skipToken
  );

  const onSrcError = useCallback((): ImageSrcErrorResult => {
    if (
      !fetchedImageSource ||
      !isAwsPresignedUrlExpired(fetchedImageSource) ||
      lastExpiryRefetchSrc.current === fetchedImageSource
    ) {
      return { recover: false };
    }
    lastExpiryRefetchSrc.current = fetchedImageSource;
    refetch();
    return { recover: true };
  }, [fetchedImageSource, refetch]);

  return (
    <div ref={visibilityRef} className="relative w-full h-full">
      <Image
        image={
          image
            ? {
                ...image,
                src: fetchedImageSource,
                isSrcLoading: !shouldFetch || isFetchingImageSource,
                isSrcError: Boolean(fetchingImageSourceError),
                onSrcError,
              }
            : undefined
        }
        onImageLoaded={onImageLoaded}
        renderErrorOverlay={renderErrorOverlay}
        className={{ image: className }}
      />
    </div>
  );
};

interface ThumbnailWrapperProps extends PropsWithChildren {
  dimensions: Dimensions;
  className?: string;
}
export const ThumbnailWrapper: FC<ThumbnailWrapperProps> = ({
  dimensions,
  className,
  children,
}) => (
  <div
    className={classes("relative grow-0 shrink-0", className)}
    style={dimensions}
  >
    {children}
  </div>
);
